/**
 * ReindexPromptModal — post-upload confirmation modal that asks the operator
 * whether to run an incremental Knowledge Atlas reindex now that new
 * artefact(s) have landed. Extracted byte-verbatim from
 * KnowledgeGraphPage.jsx in SPD v5.53 Phase E.
 *
 * Contract:
 *   - reindexPrompt   : { partition, since, upload_count } | null (parent-owned)
 *   - reindexRunning  : boolean (parent-owned)
 *   - reindexProgress : { phase, progress } | null (parent-owned, async poll UI)
 *   - onConfirm       : () => void  (fires the /api/ka/reindex call in parent)
 *   - onSkip          : () => void  (dismisses the modal in parent)
 *
 * State is deliberately parent-owned; this sub-component is a pure leaf.
 * Copy is operator-verbatim per Mockup v0.6 (2026-07-06).
 */
import React from 'react';

const PHASE_LABELS = {
  queued:  'Queued',
  m3:      'Reading your new sources',
  m4:      'Connecting them to what the Atlas already knows',
  running: 'Refreshing',
  done:    'Almost done',
  failed:  'Failed',
};

export default function ReindexPromptModal({
  reindexPrompt,
  reindexRunning,
  reindexProgress,
  onConfirm,
  onSkip,
}) {
  if (!reindexPrompt) return null;
  const progressPct = Math.max(0, Math.min(100, reindexProgress?.progress ?? 0));
  const phaseLabel = reindexProgress?.phase
    ? (PHASE_LABELS[reindexProgress.phase] || reindexProgress.phase)
    : null;
  return (
    <div
      data-testid="ka-reindex-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={reindexRunning ? undefined : onSkip}
      />
      <div
        className="relative z-10 w-full max-w-lg rounded-xl border border-cyan-400/40 bg-slate-950/95 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: '#22d3ee' }}
            aria-hidden="true"
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300/80">
            Knowledge Atlas · new artefact detected
          </span>
        </div>
        <h3
          data-testid="ka-reindex-modal-title"
          className="mb-3 text-xl font-semibold text-cyan-50"
        >
          Do you want to refresh the Knowledge Atlas now?
        </h3>
        <p
          data-testid="ka-reindex-modal-body"
          className="mb-5 text-lg leading-relaxed text-cyan-100/85"
        >
          Updating will allow the AI to draw new connections between
          subtopics and concepts immediately.
        </p>
        <div className="mb-5 rounded border border-cyan-400/20 bg-slate-900/60 px-3 py-2 text-[11px] text-cyan-200/80">
          <div>
            <span className="font-mono text-cyan-300/60">Topic partition:</span>{' '}
            <span data-testid="ka-reindex-partition" className="text-cyan-100">
              {reindexPrompt.partition}
            </span>
          </div>
          <div>
            <span className="font-mono text-cyan-300/60">Artefacts uploaded:</span>{' '}
            <span data-testid="ka-reindex-count" className="text-cyan-100">
              {reindexPrompt.upload_count}
            </span>
          </div>
        </div>
        {reindexRunning && (
          <div
            data-testid="ka-reindex-progress"
            className="mb-5 rounded border border-cyan-400/30 bg-slate-900/70 px-3 py-3"
          >
            <div className="mb-2 flex items-center justify-between text-[11px] text-cyan-100/85">
              <span data-testid="ka-reindex-phase">
                {phaseLabel || 'Refreshing…'}
              </span>
              <span
                data-testid="ka-reindex-progress-pct"
                className="font-mono text-cyan-200"
              >
                {progressPct}%
              </span>
            </div>
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800"
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full bg-cyan-400 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="mt-2 text-[10px] leading-snug text-cyan-200/60">
              Feel free to carry on — this keeps running in the
              background. Your Atlas will refresh on its own once
              it&apos;s ready.
            </p>
          </div>
        )}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid="ka-reindex-skip-btn"
            onClick={onSkip}
            disabled={reindexRunning}
            className="rounded border border-slate-500/40 bg-slate-800/60 px-4 py-2 text-sm text-slate-200 transition-colors hover:border-slate-400 hover:text-white disabled:opacity-50"
          >
            Not now
          </button>
          <button
            type="button"
            data-testid="ka-reindex-confirm-btn"
            onClick={onConfirm}
            disabled={reindexRunning}
            className="inline-flex items-center gap-2 rounded border border-cyan-400/60 bg-cyan-500/20 px-4 py-2 text-sm font-semibold text-cyan-100 transition-colors hover:border-cyan-300 hover:bg-cyan-500/30 disabled:opacity-60"
          >
            {reindexRunning ? (
              <>
                <svg
                  className="h-4 w-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <circle cx="12" cy="12" r="9" opacity="0.25" />
                  <path d="M21 12a9 9 0 0 0-9-9" />
                </svg>
                Refreshing…
              </>
            ) : (
              <>Refresh Knowledge Atlas</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
